import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import {
  CLIENT_REVIEWS_STATS,
  CLIENT_TESTIMONIALS,
  BUSINESS_NAME
} from '../data/clientReviewsPageData';
import {
  SITE_ORIGIN,
  OG_SHARE_IMAGE_URL,
  OG_SHARE_IMAGE_WIDTH,
  OG_SHARE_IMAGE_HEIGHT,
  SITE_BRAND_LEGACY
} from '../constants/siteBranding';
import './ClientReviewsPage.css';

const CANONICAL_PATH = '/client-reviews-ahmedabad';
const WHATSAPP_HREF = 'https://wa.me/919265472938';

const META_TITLE = 'Client Reviews Ahmedabad | Khodal Housekeeping Agency';
const META_DESCRIPTION = `Read real client reviews for Khodal Housekeeping Agency (formerly ${SITE_BRAND_LEGACY}) in Ahmedabad: housekeeping, valet parking, manpower and facility management. Serving SG Highway, Satellite, Bodakdev, Thaltej, Bopal, Narol and Gujarat. Trusted by 100+ clients. Call +91 92654 72938.`;

function buildReviewJsonLd(testimonials, canonicalUrl) {
  const itemListElement = testimonials.map((t, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Review',
      author: { '@type': 'Person', name: t.name },
      reviewRating: { '@type': 'Rating', ratingValue: String(t.rating), bestRating: '5', worstRating: '1' },
      reviewBody: t.text,
      itemReviewed: {
        '@type': 'LocalBusiness',
        name: BUSINESS_NAME,
        url: SITE_ORIGIN
      }
    }
  }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: 'What Our Clients Say About Us',
        description: META_DESCRIPTION,
        isPartOf: { '@type': 'WebSite', name: BUSINESS_NAME, url: SITE_ORIGIN }
      },
      {
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#reviews`,
        name: 'Client testimonials',
        numberOfItems: testimonials.length,
        itemListElement
      }
    ]
  };
}

function ClientReviewsPage() {
  const canonical = `${SITE_ORIGIN}${CANONICAL_PATH}`;
  const reviewSchema = useMemo(
    () => buildReviewJsonLd(CLIENT_TESTIMONIALS, canonical),
    [canonical]
  );

  return (
    <div className="client-reviews-page">
      <Helmet>
        <title>{META_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} />
        <meta
          name="keywords"
          content="Khodal reviews Ahmedabad, client testimonials, housekeeping reviews Ahmedabad, valet parking reviews Ahmedabad, manpower services Ahmedabad, facility management reviews Gujarat, SG Highway cleaning service reviews"
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={META_TITLE} />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:image" content={OG_SHARE_IMAGE_URL} />
        <meta property="og:image:width" content={String(OG_SHARE_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(OG_SHARE_IMAGE_HEIGHT)} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={META_TITLE} />
        <meta name="twitter:description" content={META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_SHARE_IMAGE_URL} />
        <meta name="twitter:image:alt" content="Khodal Housekeeping Agency — client reviews and testimonials Ahmedabad" />
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>

      <Header />

      <main className="client-reviews-main">
        <header className="client-reviews-hero">
          <div className="container">
            <nav className="client-reviews-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true"> / </span>
              <span>Client reviews</span>
            </nav>
            <h1 className="client-reviews-h1">What Our Clients Say About Us</h1>
            <p className="client-reviews-sub">
              Our clients trust us for valet parking, manpower, and housekeeping services in Ahmedabad. Here are real
              experiences shared by our happy clients.
            </p>
            <ul className="client-reviews-stats">
              {CLIENT_REVIEWS_STATS.map((s) => (
                <li key={s.label}>
                  <span className="client-reviews-stat-emoji" aria-hidden="true">
                    {s.emoji}
                  </span>
                  <span className="client-reviews-stat-value">{s.value}</span>
                  <span className="client-reviews-stat-label">{s.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </header>

        <div className="client-reviews-body">
          <p className="client-reviews-intro">
            {BUSINESS_NAME} supports businesses and homes across Ahmedabad with dependable{' '}
            <Link to="/valet-parking-ahmedabad">valet parking in Ahmedabad</Link>, flexible{' '}
            <Link to="/manpower-services-ahmedabad">manpower services in Ahmedabad</Link>, and thorough{' '}
            <Link to="/housekeeping-services-ahmedabad">housekeeping services in Ahmedabad</Link>. The reviews below
            reflect real feedback on punctuality, sanitised equipment, transparent pricing, and professional crews—so
            you can shortlist us with confidence for your next contract or one-time service.
          </p>

          <ul className="client-reviews-grid">
            {CLIENT_TESTIMONIALS.map((t) => (
              <li key={t.id}>
                <article className="client-reviews-card">
                  <span className="client-reviews-quote" aria-hidden="true">
                    “
                  </span>
                  <div className="client-reviews-stars" aria-label={`${t.rating} out of 5 stars`} role="img">
                    <span className="client-reviews-stars-fill">
                      {'★'.repeat(Math.min(5, Math.max(0, t.rating)))}
                    </span>
                    {t.rating < 5 && (
                      <span className="client-reviews-stars-empty" aria-hidden="true">
                        {'☆'.repeat(5 - t.rating)}
                      </span>
                    )}
                  </div>
                  <p className="client-reviews-name">{t.name}</p>
                  <p className="client-reviews-text">{t.text}</p>
                  {t.tags && t.tags.length > 0 && (
                    <ul className="client-reviews-tags">
                      {t.tags.map((tag) => (
                        <li key={tag}>{tag}</li>
                      ))}
                    </ul>
                  )}
                </article>
              </li>
            ))}
          </ul>

          <section className="client-reviews-cta" aria-labelledby="reviews-cta-heading">
            <h2 id="reviews-cta-heading">Ready to experience the same service?</h2>
            <p>
              Share your requirement for Ahmedabad—offices, societies, events, or homes—and we will propose the right
              crew and schedule.
            </p>
            <div className="client-reviews-cta-actions">
              <a href="tel:+919265472938" className="client-reviews-cta-btn primary">
                Call +91 9265472938
              </a>
              <a href={WHATSAPP_HREF} className="client-reviews-cta-btn secondary" target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
              <Link to="/inquiry" className="client-reviews-cta-btn secondary">
                Get a quote
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ClientReviewsPage;
